// Character metadata: original use and security risks
export const CHARACTER_METADATA: Record<number, { originalUse: string; securityRisks: string[] }> = {
  // Control Characters
  0x0000: {
    originalUse: 'Null character used for padding and stream termination',
    securityRisks: ['String truncation attacks', 'Buffer overflow exploitation', 'Null byte injection']
  },
  0x0009: {
    originalUse: 'Tab character for horizontal indentation and alignment',
    securityRisks: ['Tab-space confusion attacks', 'Log poisoning via mixed whitespace', 'Width-based bypasses']
  },
  0x000A: {
    originalUse: 'Line feed for end-of-line marker in Unix systems',
    securityRisks: ['Header injection attacks', 'Log poisoning and manipulation', 'CRLF injection']
  },
  0x000D: {
    originalUse: 'Carriage return for end-of-line marker in legacy systems',
    securityRisks: ['CRLF injection attacks', 'HTTP response splitting', 'Email header injection']
  },
  0x001B: {
    originalUse: 'Escape character for ANSI terminal sequences and control codes',
    securityRisks: ['Terminal escape sequence injection', 'Log output manipulation', 'Visual spoofing attacks']
  },
  0x007F: {
    originalUse: 'Delete character for erasing previous character',
    securityRisks: ['Character hiding in terminal output', 'Obfuscation of malicious content', 'Visual confusion']
  },

  // Zero-Width Characters
  0x200B: {
    originalUse: 'Zero-width space for word breaking without visible space',
    securityRisks: ['Steganography and data hiding', 'Phishing URL obfuscation', 'Fingerprinting GenAI content']
  },
  0x200C: {
    originalUse: 'Zero-width non-joiner for script language separation',
    securityRisks: ['Homoglyph attacks with script mixing', 'Domain name spoofing', 'Invisible character insertion']
  },
  0x200D: {
    originalUse: 'Zero-width joiner for character ligature formation',
    securityRisks: ['Emoji manipulation attacks', 'Script mixing exploits', 'Visual character transformation']
  },
  0x2060: {
    originalUse: 'Word joiner for preventing word breaks',
    securityRisks: ['Invisible character injection', 'Text manipulation', 'Steganography encoding']
  },
  0xFEFF: {
    originalUse: 'Byte order mark (BOM) for encoding identification',
    securityRisks: ['Encoding confusion attacks', 'UTF-8 BOM bypass filters', 'XML external entity injection']
  },

  // Directional Characters
  0x200E: {
    originalUse: 'Left-to-right mark for explicit text direction',
    securityRisks: ['BiDi override attacks', 'URL direction spoofing', 'Filename spoofing']
  },
  0x200F: {
    originalUse: 'Right-to-left mark for explicit text direction',
    securityRisks: ['BiDi override attacks', 'Domain name spoofing (rtl domains)', 'Visual character reversal']
  },
  0x202A: {
    originalUse: 'Left-to-right embedding for nested directional text',
    securityRisks: ['BiDi text exploitation', 'URL reversal attacks', 'Filename spoofing']
  },
  0x202B: {
    originalUse: 'Right-to-left embedding for nested directional text',
    securityRisks: ['BiDi override attacks', 'Homoglyph attacks', 'Text direction confusion']
  },
  0x202C: {
    originalUse: 'Pop directional formatting to close embeddings',
    securityRisks: ['BiDi context manipulation', 'Text reordering exploits', 'Visual spoofing']
  },
  0x202D: {
    originalUse: 'Left-to-right override for forced text direction',
    securityRisks: ['Text reversal attacks', 'URL spoofing', 'Filename obfuscation']
  },
  0x202E: {
    originalUse: 'Right-to-left override for forced text direction',
    securityRisks: ['Right-to-left override (RLO) attacks', 'File extension hiding', 'Executable masquerading']
  },
  0x061C: {
    originalUse: 'Arabic letter mark for explicit directionality in Arabic text',
    securityRisks: ['BiDi spoofing attacks', 'Homograph attacks with Arabic characters', 'Text direction manipulation']
  },

  // Space Characters
  0x0020: {
    originalUse: 'Regular space for word separation',
    securityRisks: ['Space-based URL obfuscation', 'Log injection with mixed spaces', 'Whitespace normalization bypass']
  },
  0x00A0: {
    originalUse: 'Non-breaking space to prevent word breaks',
    securityRisks: ['Invisible space injection', 'URL manipulation', 'Layout manipulation']
  },
  0x1680: {
    originalUse: 'Ogham space mark for Ogham script spacing',
    securityRisks: ['Invisible space insertion', 'Text processing confusion', 'Whitespace normalization bypass']
  },
  0x2000: {
    originalUse: 'En quad for typographic spacing',
    securityRisks: ['Invisible whitespace injection', 'Text normalization bypass', 'Space-based attacks']
  },
  0x2001: {
    originalUse: 'Em quad for typographic spacing',
    securityRisks: ['Invisible space insertion', 'Width-based bypasses', 'Whitespace manipulation']
  },
  0x2002: {
    originalUse: 'En space for typographic spacing',
    securityRisks: ['Invisible character injection', 'URL obfuscation', 'Layout attack vector']
  },
  0x2003: {
    originalUse: 'Em space for typographic spacing',
    securityRisks: ['Invisible space hiding', 'Text processing confusion', 'Whitespace normalization bypass']
  },
  0x2004: {
    originalUse: 'Three-per-em space for typographic spacing',
    securityRisks: ['Invisible character insertion', 'Width-based bypasses', 'Text manipulation']
  },
  0x2005: {
    originalUse: 'Four-per-em space for typographic spacing',
    securityRisks: ['Invisible space injection', 'Text normalization bypass', 'Layout manipulation']
  },
  0x2006: {
    originalUse: 'Six-per-em space for typographic spacing',
    securityRisks: ['Invisible character hiding', 'Whitespace-based attacks', 'Text processing confusion']
  },
  0x2007: {
    originalUse: 'Figure space for aligning numerals',
    securityRisks: ['Invisible space insertion', 'Numeric alignment exploitation', 'Layout attack']
  },
  0x2008: {
    originalUse: 'Punctuation space for spacing punctuation',
    securityRisks: ['Invisible character injection', 'Text manipulation', 'Whitespace normalization bypass']
  },
  0x2009: {
    originalUse: 'Thin space for narrow typographic spacing',
    securityRisks: ['Invisible space hiding', 'Width-based bypasses', 'Text processing confusion']
  },
  0x200A: {
    originalUse: 'Hair space for thinnest typographic spacing',
    securityRisks: ['Invisible character insertion', 'Steganography encoding', 'Whitespace manipulation']
  },
  0x202F: {
    originalUse: 'Narrow no-break space for French punctuation',
    securityRisks: ['Invisible space insertion', 'Whitespace normalization bypass', 'Language-specific confusion']
  },
  0x205F: {
    originalUse: 'Medium mathematical space for equations',
    securityRisks: ['Invisible character in math context', 'Text processing confusion', 'Width manipulation']
  },
  0x3000: {
    originalUse: 'Ideographic space for CJK character spacing',
    securityRisks: ['Invisible space injection', 'CJK text manipulation', 'Whitespace normalization bypass']
  },

  // Soft Hyphen
  0x00AD: {
    originalUse: 'Soft hyphen for optional line breaking',
    securityRisks: ['URL disguise and obfuscation', 'Homoglyph attacks', 'Character hiding in text']
  },

  // Format Characters
  0x180E: {
    originalUse: 'Mongolian vowel separator for Mongolian script',
    securityRisks: ['Script-specific text manipulation', 'Invisible character injection', 'Text processing bypass']
  },
  0x2066: {
    originalUse: 'Left-to-right isolate for isolating text direction',
    securityRisks: ['BiDi isolation bypasses', 'Text direction confusion', 'Visual spoofing']
  },
  0x2067: {
    originalUse: 'Right-to-left isolate for isolating text direction',
    securityRisks: ['BiDi manipulation', 'Direction confusion attacks', 'URL spoofing']
  },
  0x2068: {
    originalUse: 'First strong isolate for directional content detection',
    securityRisks: ['BiDi context manipulation', 'Text direction exploitation', 'Visual confusion']
  },
  0x2069: {
    originalUse: 'Pop directional isolate to close isolate formatting',
    securityRisks: ['BiDi formatting bypass', 'Text reordering attacks', 'Visual spoofing']
  },
  0xFFF9: {
    originalUse: 'Interlinear annotation anchor for ruby text',
    securityRisks: ['Annotation injection attacks', 'Text manipulation', 'Rendering confusion']
  },
  0xFFFA: {
    originalUse: 'Interlinear annotation separator for ruby text',
    securityRisks: ['Annotation-based injection', 'Text processing confusion', 'Rendering manipulation']
  },
  0xFFFB: {
    originalUse: 'Interlinear annotation terminator for ruby text',
    securityRisks: ['Annotation context escaping', 'Text rendering bypass', 'Display manipulation']
  },
};

export const getCharacterMetadata = (code: number) => {
  return CHARACTER_METADATA[code] || {
    originalUse: 'Character use and purpose not documented',
    securityRisks: ['Potentially undocumented security implications']
  };
};

// ===== PUA DETECTION METADATA =====
/**
 * Metadata for Private Use Area (PUA) character ranges
 * @internal - Currently unused, kept for potential future UI/documentation features
 */
export const PUA_METADATA = {
  BMP_PUA: {
    range: '0xE000-0xF8FF',
    size: 6400,
    originalUse: 'Custom character assignments for font glyphs, gaiji, and proprietary implementations',
    knownPatterns: [
      { name: 'Font Awesome', range: '0xF000-0xF2FF', risk: 'LOW', description: 'Icon font library' },
      { name: 'Material Design Icons', range: '0xE000-0xE3FF', risk: 'LOW', description: 'Google Material icon font' },
      { name: 'CJK Gaiji', range: '0xE000-0xE0FF', risk: 'LOW', description: 'Rare East Asian characters' },
      { name: 'Steganography/Hidden Data', range: 'Scattered', risk: 'CRITICAL', description: 'Malicious data encoding' }
    ],
    securityRisks: [
      'Steganography and data hiding',
      'Filter evasion by replacing characters with PUA equivalents',
      'Command/code injection bypassing filters',
      'Phishing through PUA character substitution',
      'Tracking and fingerprinting via watermarking',
      'No-font rendering = invisible to users'
    ],
    detectionHint: 'High density PUA outside icon font contexts suggests malicious use'
  },
  Plane_15_PUA: {
    range: '0xF0000-0xFFFFD',
    size: 65534,
    originalUse: 'Extended private use area for specialized implementations',
    securityRisks: ['Large-scale steganography capacity', 'Data exfiltration via PUA encoding']
  },
  Plane_16_PUA: {
    range: '0x100000-0x10FFFD',
    size: 65534,
    originalUse: 'Extended private use area for highly specialized implementations',
    securityRisks: ['Largest steganography capacity', 'Requires surrogate pair encoding']
  },
};

// ===== TAG BLOCK METADATA =====
/**
 * Metadata for Tag Block characters (U+E0000–U+E007F)
 * @internal - Currently unused, kept for potential future UI/documentation features
 */
export const TAG_BLOCK_METADATA = {
  range: '0xE0000-0xE007F',
  size: 128,
  originalUse: 'Language and variant tagging for emoji sequences (e.g., flag emojis)',
  commonUse: 'Flag emoji composition via region indicator sequences',
  securityRisks: [
    'Invisible in most contexts (no visual representation)',
    'Token expansion attacks on LLMs',
    'Smuggling hidden commands in emoji sequences',
    'Bypassing AI safety guardrails',
    'Data encoding for steganography',
    '2024 Threat: Emoji attack on LLMs (Wei et al., 2024)'
  ],
  threatLevel: 'HIGH' as const,
};

// ===== FULLWIDTH CHARACTER METADATA (CVE-2024-4577) =====
/**
 * Metadata for Fullwidth character exploitation vulnerabilities
 * @internal - Currently unused, kept for potential future UI/documentation features
 */
export const FULLWIDTH_METADATA = {
  range: '0xFF00-0xFFEF',
  originalUse: 'Full-width variants of ASCII characters for CJK text display',
  commonChars: [
    { code: 0xFF0E, char: '．', name: 'Fullwidth Full Stop', normalizesSo: '.', risk: 'HIGH' },
    { code: 0xFF1C, char: '＜', name: 'Fullwidth Less-Than', normalizesSo: '<', risk: 'CRITICAL' },
    { code: 0xFF1E, char: '＞', name: 'Fullwidth Greater-Than', normalizesSo: '>', risk: 'CRITICAL' },
    { code: 0xFF3C, char: '＼', name: 'Fullwidth Backslash', normalizesSo: '\\', risk: 'CRITICAL' },
  ],
  securityRisks: [
    'CVE-2024-4577: PHP-CGI path traversal via fullwidth normalization',
    'Bypasses input validation that runs before normalization',
    'Command injection when normalized by shell/interpreter',
    'Path traversal attacks using fullwidth slashes and dots',
    'HTML injection via fullwidth angle brackets',
    'XSS attacks with fullwidth < and > characters'
  ],
  keyTechnique: 'Normalization-after-validation vulnerability - validate before normalizing!',
  threatLevel: 'CRITICAL' as const,
};

// ===== 2024 UNICODE ATTACK METADATA =====
/**
 * Comprehensive metadata for 2024 Unicode security threats
 * @internal - Currently unused, kept for potential future UI/documentation features
 */
export const UNICODE_2024_THREATS = {
  emojiTokenExpansion: {
    name: 'Emoji Token Expansion / Token Segmentation Bias',
    year: 2024,
    references: 'Wei et al., "Emoji Attack" (arXiv:2411.01077v1)',
    affected: ['Llama Guard', 'Llama Guard 2', 'ShieldLM', 'GPT-3.5', 'GPT-4'],
    technique: 'Strategic emoji insertion causes unexpected tokenization, bypassing AI safety filters',
    bypassRates: '12-75% depending on model',
    indicators: 'Unusual emoji placement within harmful text, non-natural positions',
    threat: 'CRITICAL'
  },
  zeroWidthTagSmuggling: {
    name: 'Zero-Width Character & Tag Block Smuggling in LLMs',
    year: 2024,
    technique: 'Invisible Unicode (U+E0000–U+E007F) hides malicious prompts/commands',
    vectors: ['Prompt injection', 'Token expansion attacks', 'Unbounded consumption DoS'],
    threat: 'CRITICAL'
  },
  trojanSourceBiDi: {
    name: 'Trojan Source (BiDi Exploits)',
    cve: 'CVE-2021-42574',
    technique: 'BiDi override characters reorder source code visually vs. execution',
    affected: 'All programming languages',
    threat: 'CRITICAL'
  },
  normalizationVulnerabilities: {
    name: 'Unicode Normalization-After-Validation',
    cves: ['CVE-2024-34078', 'CVE-2024-4577', 'CVE-2024-43093'],
    technique: 'Characters bypass validation, then normalize to dangerous forms',
    examples: 'Fullwidth→ASCII, ﻿→<, ．→.',
    threat: 'CRITICAL'
  },
  idnHomographs: {
    name: 'IDN Homograph Attacks',
    technique: 'Visually identical domains using different Unicode scripts',
    examples: ['аpple.com (Cyrillic а)', 'раypal.com (Cyrillic р and а)', 'Αmazon.com (Greek Α)'],
    threat: 'HIGH'
  }
};
